import { useEffect, useState } from "react";
import { Alert, Box, Button, Grid } from "@mui/material";

import LocalizationFields from "../../../../components/forms/LocalizationFields";
import { useDisplayLocalization } from "../../../../lib/datetime/DisplayLocalizationProvider";
import {
  customerService,
  getCustomerApiErrorMessage,
  type Customer,
} from "../../../../services/customerService";

type CustomerLocalizationEditorProps = {
  customer: Customer;
  onUpdated: (customer: Customer) => void;
};

const CustomerLocalizationEditor = ({ customer, onUpdated }: CustomerLocalizationEditorProps) => {
  const { apply, reload } = useDisplayLocalization();
  const [timeZone, setTimeZone] = useState(customer.timeZone || "Europe/Istanbul");
  const [locale, setLocale] = useState(customer.locale || "tr-TR");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setTimeZone(customer.timeZone || "Europe/Istanbul");
    setLocale(customer.locale || "tr-TR");
  }, [customer.id, customer.timeZone, customer.locale]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await customerService.updateMyLocalization(customer, { timeZone, locale });
      const nextLocalization = {
        timeZone,
        locale,
      };
      const nextCustomer = { ...updated, ...nextLocalization };
      onUpdated(nextCustomer);
      apply(nextLocalization, nextCustomer);
      await reload();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            title: "Profile updated",
            content: `Timezone saved as ${nextLocalization.timeZone}.`,
            dateTime: Date.now(),
          },
        }),
      );
    } catch (error) {
      const message = getCustomerApiErrorMessage(
        error,
        "Timezone and locale could not be saved. Please try again.",
      );
      setSaveError(message);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "error",
            title: "Profile update failed",
            content: message,
            dateTime: Date.now(),
          },
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2.5} alignItems="flex-start">
        <LocalizationFields
          timeZone={timeZone}
          locale={locale}
          onTimeZoneChange={setTimeZone}
          onLocaleChange={setLocale}
        />
        {saveError ? (
          <Grid size={12}>
            <Alert severity="error">{saveError}</Alert>
          </Grid>
        ) : null}
        <Grid size={12}>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || !timeZone || !locale}
          >
            {saving ? "Saving..." : "Save localization"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerLocalizationEditor;
